import React from 'react';
import { useForm } from 'react-hook-form';
import { Form, Button, FloatingLabel, Stack } from 'react-bootstrap';
import { formatDateInput, validateDateComplete, validateDateFormat, formatToISODate, formatFromISODate, validateNotFuture } from '@odmonorepo/odfunctions';

export interface OwnerFormData {
  firstName: string;
  lastName: string;
  address: {
    zipcode: string;
    addressLine1: string;
    city: string;
    state: string;
  };
  homePhone: string;
  dob: string;
  ownership: number;
  isPrimaryOwner: string;
  ssn: string;
}

interface OwnerFormProps {
  initialFormData?: OwnerFormData;
  onSubmit?: (data: OwnerFormData) => void;
}

export const PersonalForm = ({
  initialFormData,
  onSubmit = () => {},
}: OwnerFormProps) => {
  // Convert ISO date to display format when loading
  const formattedInitialData = initialFormData
    ? {
        ...initialFormData,
        dob: initialFormData.dob ? formatFromISODate(initialFormData.dob) : '',
      }
    : undefined;

  const { register, handleSubmit } = useForm<OwnerFormData>({
    defaultValues: formattedInitialData,
  });

  // Convert display format to ISO format when submitting
  const handleFormSubmit = (data: OwnerFormData) => {
    const submissionData = {
      ...data,
      dob: data.dob ? formatToISODate(data.dob) : '',
    };
    onSubmit(submissionData);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack gap={3}>
        <FloatingLabel controlId="firstName" label="First Name">
          <Form.Control type="text" required {...register('firstName')} />
        </FloatingLabel>

        <FloatingLabel controlId="lastName" label="Last Name">
          <Form.Control type="text" required {...register('lastName')} />
        </FloatingLabel>

        <FloatingLabel controlId="addressLine1" label="Address Line 1">
          <Form.Control
            type="text"
            required
            {...register('address.addressLine1')}
          />
        </FloatingLabel>

        <Stack direction="horizontal" gap={3}>
          <FloatingLabel controlId="city" label="City">
            <Form.Control type="text" required {...register('address.city')} />
          </FloatingLabel>

          <FloatingLabel controlId="state" label="State">
            <Form.Control type="text" required {...register('address.state')} />
          </FloatingLabel>

          <FloatingLabel controlId="zipcode" label="Zipcode">
            <Form.Control
              type="text"
              required
              inputMode="numeric"
              {...register('address.zipcode')}
            />
          </FloatingLabel>
        </Stack>

        <FloatingLabel controlId="homePhone" label="Home Phone">
          <Form.Control type="tel" required {...register('homePhone')} />
        </FloatingLabel>

        <FloatingLabel controlId="dateOfBirth" label="Date of Birth">
          <Form.Control 
            type="text" 
            required 
            {...register('dob', { 
              validate: {
                complete: (v) => validateDateComplete(v) || 'Invalid date format',
                format: (v) => validateDateFormat(v) || 'Invalid date format',
                notFuture: (v) => validateNotFuture(v) || 'Date of birth must be in the past'
              }
            })} 
            maxLength={10}
            inputMode="numeric"
            onChange={(e) => {
              e.target.value = formatDateInput(e.target.value);
              register('dob').onChange(e);
            }}
          />
        </FloatingLabel>

        <FloatingLabel
          controlId="ownership"
          label="Business Ownership Percentage"
        >
          <Form.Control type="number" required inputMode="numeric" {...register('ownership')} />
        </FloatingLabel>

        <FloatingLabel controlId="ssn" label="Social Security Number">
          <Form.Control type="text" required inputMode="numeric" {...register('ssn')} />
        </FloatingLabel>

        <Button variant="primary" type="submit" className="mt-3">
          Submit
        </Button>
      </Stack>
    </Form>
  );
};

export default PersonalForm;
